import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useSubmitEarningExpenseMutation } from "@services/knowYourCustomer/mutations";
import { useEarningExpenseQuery } from "@services/knowYourCustomer/queries";
import {
  KycFormStep,
  type IEarningExpense,
  type TKnowYourCustomerStepAction,
} from "@services/knowYourCustomer/types";
import { KYCModalActionButtons } from "../KYCModalActionButtons/KYCModalActionButtons";
import { KYCModalPaging } from "../KYCModalPaging/KYCModalPaging";

interface IEarningExpenseFormProps {
  onChangeStep: (step: KycFormStep) => void;
}

export const EarningExpenseForm: React.FC<IEarningExpenseFormProps> = ({
  onChangeStep,
}) => {
  const { t } = useTranslation();
  const { data: earningExpense } = useEarningExpenseQuery();

  const { mutateAsync: submitEarningExpense, isPending } =
    useSubmitEarningExpenseMutation();

  const { handleSubmit, control, setError } = useForm<IEarningExpense>({
    defaultValues: {
      monthlyEarning: "",
      monthlyPayment: "",
    },
    values: earningExpense,
  });

  const { monthlyEarningOptions, monthlyPaymentOptions } = useMemo(() => {
    if (!earningExpense) {
      return { monthlyEarningOptions: [], monthlyPaymentOptions: [] };
    }

    const monthlyEarningOptions =
      earningExpense.monthlyEarnings?.map((monthlyEarning) => ({
        value: monthlyEarning,
        label: t(
          `KYC.modal.step4.form.fields.monthlyEarning.options.${monthlyEarning}`,
        ),
      })) ?? [];

    const monthlyPaymentOptions =
      earningExpense.monthlyPayments?.map((monthlyPayment) => ({
        value: monthlyPayment,
        label: t(
          `KYC.modal.step4.form.fields.monthlyPayment.options.${monthlyPayment}`,
        ),
      })) ?? [];

    return { monthlyEarningOptions, monthlyPaymentOptions };
  }, [earningExpense, t]);

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitEarningExpenseForm = async (
    data: IEarningExpense,
    step?: TKnowYourCustomerStepAction,
  ) => {
    delete data.monthlyEarnings;
    delete data.monthlyPayments;

    await submitEarningExpense({ ...data, step })
      .then((response) => onChangeStep(response.currentForm))
      .catch(setApiErrors);
  };

  return (
    <div className="flex flex-col gap-6">
      <KYCModalPaging
        currentStep={KycFormStep.EARNING_EXPENSE_FORM}
        onPreviousButtonClick={handleSubmit((data) =>
          submitEarningExpenseForm(data, "previous"),
        )}
        onNextButtonClick={handleSubmit((data) =>
          submitEarningExpenseForm(data),
        )}
        isLoading={isPending}
      />
      <form
        onSubmit={handleSubmit((data) => submitEarningExpenseForm(data))}
        className="flex flex-col gap-3"
      >
        <p className="text-gray-900 p-0 text-lg font-semibold">
          {t("KYC.modal.step4.form.fields.monthlyEarning.title")}
        </p>
        <FormSelect
          name="monthlyEarning"
          options={monthlyEarningOptions}
          control={control}
          label={t("KYC.modal.step4.form.fields.monthlyEarning.label")}
        />
        <p className="text-gray-900 p-0 text-lg font-semibold">
          {t("KYC.modal.step4.form.fields.monthlyPayment.title")}
        </p>
        <FormSelect
          name="monthlyPayment"
          options={monthlyPaymentOptions}
          control={control}
          label={t("KYC.modal.step4.form.fields.monthlyPayment.label")}
        />
      </form>
      <KYCModalActionButtons
        onSubmit={handleSubmit((data) => submitEarningExpenseForm(data))}
        onCancel={handleSubmit((data) =>
          submitEarningExpenseForm(data, "close"),
        )}
        isLoading={isPending}
      />
    </div>
  );
};
